import * as yup from "yup";
import { FormikProps } from "formik";
import { getOutputGuid } from "portal/utils/ids";
import {
  hitchConfigSchema,
  polygon2DSchema,
  positiveNumberSchema,
  ptoConfigSchema,
  scvOutputsSchema,
  steeringRangeSchema,
} from "../../schema";
import { OmitPick } from "common/utils/types";
import {
  optionalFieldToViewModel,
  OptionalViewModel,
  optionalViewModelToField,
} from "common/utils/viewModels";
import {
  persistTractorBoundaryViewModel,
  tractorArchetypeBoundaryViewModel,
  tractorBoundaryViewModel,
  TractorBoundaryViewModel,
} from "../boundaryModel";
import { Polygon2D } from "protos/geometry/geometry";
import {
  ScvOutputs,
  SteeringRange,
  Tractor,
  TractorArchetype,
} from "protos/atk/autotractor";
import { setAndTouch } from "portal/utils/forms";
import { TractorCalibration } from "protos/atk/calibration";
import { typedPath } from "typed-path";

type DriveInfoStepFields = Pick<
  Tractor,
  "wheelSpacingM" | "minTurningRadiusM" | "steeringRange"
>;

type OtherExplicitFields = Pick<
  Tractor,
  "calibration" | "boundary" | "boundaryModel"
>;

export interface TractorViewModel
  extends OmitPick<Tractor, DriveInfoStepFields & OtherExplicitFields> {
  driveInfo: {
    override: boolean;
    minTurningRadiusM: number;
    steeringRange: SteeringRange;
    wheelbaseM: number;
    wheelSpacingM: number;
    // the presence of the boundary model in the written-back tractor is currently
    // governed by driveInfo.override - cqb
    boundaryViewModel: TractorBoundaryViewModel;
    // the existing boundary on the tractor object, in case the boundary model
    // can't be manipulated by Ops Center, so it can be written back unchanged
    existingBoundaryPolygon: Polygon2D | undefined;
  };
  calibration: OptionalViewModel<TractorCalibration>;
}

export const viewModelSchema = yup.object<TractorViewModel>({
  id: yup.string().optional(),
  customerId: yup.number().optional(),
  updatedAt: yup.string().optional(),
  name: yup.string().required("utils.form.required"),
  steeringRange: steeringRangeSchema.notRequired().default(undefined),
  minTurningRadiusM: positiveNumberSchema.optional(),
  wheelSpacingM: positiveNumberSchema.optional(),
  frontPto: ptoConfigSchema.optional(),
  rearPto: ptoConfigSchema.optional(),
  hitches: hitchConfigSchema.optional(),
  scvOutputs: scvOutputsSchema.optional(),
  boundary: polygon2DSchema.optional(),
  driveInfo: yup.object({
    override: yup.boolean(),
    minTurningRadiusM: positiveNumberSchema,
    steeringRange: yup.object({
      min: yup.number(),
      max: yup.number(),
    }),
    tractorDimensions: yup.object({
      wheelSpacingM: positiveNumberSchema,
      noseToFrontAxleM: positiveNumberSchema,
      overallLengthM: positiveNumberSchema,
      overallWidthM: positiveNumberSchema,
    }),
  }),
});

/**
 * this variable isn't used anywhere -- it just ensures that the schema and the
 * shape of the type it describes don't drift apart at typecheck time.
 */
const _: TractorViewModel = tractorToViewModel(
  undefined
) satisfies yup.InferType<typeof viewModelSchema>;

const S = typedPath<TractorViewModel>();

/**
 * changing the archetype for a tractor can overwrite fields in the view model.
 * handle this.
 */
export function changeArchetype(
  formik: FormikProps<TractorViewModel>,
  archetype: TractorArchetype
): void {
  setAndTouch(formik, S.archetype, archetype);

  // note: not all of this flow's state management has moved over to the view
  // model yet. this function will grow as it does.
  if (!formik.values.driveInfo.override) {
    setAndTouch(
      formik,
      S.driveInfo.minTurningRadiusM,
      archetype.minTurningRadiusM
    );
    setAndTouch(
      formik,
      S.driveInfo.steeringRange,
      archetype.steeringRange ?? { min: 0, max: 0 }
    );

    setAndTouch(formik, S.driveInfo.wheelbaseM, archetype.wheelbaseM);
    setAndTouch(formik, S.driveInfo.wheelSpacingM, archetype.wheelSpacingM);

    setAndTouch(
      formik,
      S.driveInfo.boundaryViewModel,
      tractorArchetypeBoundaryViewModel(archetype)
    );
  }

  if (!formik.values.calibration.present && archetype.calibration) {
    setAndTouch(formik, S.calibration.value, archetype.calibration);
  }
}

export function tractorToViewModel(
  existingTractor: Tractor | undefined
): TractorViewModel {
  const baseTractor = existingTractor ?? Tractor.fromPartial({});

  const boundaryViewModel = tractorBoundaryViewModel(baseTractor);
  const overrideDriveInfo =
    baseTractor.wheelSpacingM !== undefined ||
    baseTractor.steeringRange !== undefined ||
    baseTractor.minTurningRadiusM !== undefined ||
    boundaryViewModel.present;

  return {
    ...baseTractor,
    driveInfo: {
      override: overrideDriveInfo,
      wheelbaseM: baseTractor.archetype?.wheelbaseM ?? 0,
      wheelSpacingM:
        baseTractor.wheelSpacingM ?? baseTractor.archetype?.wheelSpacingM ?? 0,
      minTurningRadiusM:
        baseTractor.minTurningRadiusM ??
        baseTractor.archetype?.minTurningRadiusM ??
        0,
      steeringRange: baseTractor.steeringRange ??
        baseTractor.archetype?.steeringRange ?? { max: 0, min: 0 },
      boundaryViewModel: boundaryViewModel.value,
      existingBoundaryPolygon: baseTractor.boundary,
    },
    calibration: optionalFieldToViewModel(
      // doesn't check archetype because we shouldn't write back a calibration to
      // the tractor automatically if the archetype has one
      baseTractor.calibration,
      baseTractor.archetype?.calibration ?? {
        gpsPosXMm: 0,
        gpsPosYMm: 0,
        gpsHeadingOffsetDeg: 0,
        steeringCenterTrimDeg: 0,
        steeringLeftDeadbandMa: 0,
        steeringRightDeadbandMa: 0,
        steeringControlSignalMinMa: 0,
        steeringControlSignalMaxMa: 0,
        extraConfigs: [],
      }
    ),
  };
}

export function viewModelToTractor(viewModel: TractorViewModel): Tractor {
  let scvOutputs: ScvOutputs | undefined;

  if (viewModel.scvOutputs) {
    scvOutputs = {
      scvOutput: viewModel.scvOutputs.scvOutput.map((o) => {
        const newOutput = { ...o };
        if (!o.id) {
          newOutput.id = getOutputGuid();
        }
        return o;
      }),
    };
  }

  const overriddenDriveInfo: DriveInfoStepFields | undefined = viewModel
    .driveInfo.override
    ? {
        wheelSpacingM: viewModel.driveInfo.wheelSpacingM,
        minTurningRadiusM: viewModel.driveInfo.minTurningRadiusM,
        steeringRange: viewModel.driveInfo.steeringRange,
      }
    : {
        wheelSpacingM: undefined,
        minTurningRadiusM: undefined,
        steeringRange: undefined,
      };

  return {
    ...viewModel,
    ...overriddenDriveInfo,
    ...(viewModel.driveInfo.override
      ? persistTractorBoundaryViewModel(
          viewModel.driveInfo.boundaryViewModel,
          viewModel.driveInfo.existingBoundaryPolygon
        )
      : {
          boundary: viewModel.driveInfo.existingBoundaryPolygon,
          boundaryModel: undefined,
        }),
    scvOutputs,
    calibration: optionalViewModelToField(viewModel.calibration),
    captureCompleted: true,
  };
}

/**
 * A reusable hook which allows us to enable and disable editing for different
 * tractor data model versions cleanly without having to manually modify a bunch
 * of individual forms.
 * @param _viewModel the tractor view model (currently unused)
 * @returns true if editing the tractor view model is supported, false otherwise
 */
export function editingSupported(_viewModel: TractorViewModel): boolean {
  return true;
}
